/**
 * MerchantOverlay — STS-style between-wave merchant shown every 3 waves.
 *
 * Usage:
 *   const overlay = new MerchantOverlay(scene, payload, playerState, {
 *     onPick:    (index) => { ... apply effect ... },
 *     onDismiss: ()      => { ... no buff ...       },
 *   });
 *   overlay.show();
 *
 * TODO (wire-up):
 *   In HorizontalBattleScene, listen for the `merchant:show` event emitted by
 *   WaveController, construct a MerchantOverlay, call `.show()`, and on
 *   `onPick`/`onDismiss` call `waveController.resumeFromMerchant()`.
 *   The UX agent currently owns horizontal-battle-scene.ts — do NOT edit it here.
 *
 * Events emitted (via callbacks, not Phaser events — keeps overlay self-contained):
 *   onPick(index: 0|1|2) — player tapped a card
 *   onDismiss()          — 3-second timeout with no pick
 */

import type { MerchantOption, MerchantPayload } from '../systems/wave-controller';

/** Minimal Phaser Scene interface needed to build the overlay (mock-friendly). */
export interface OverlayScene {
  add: {
    container: (x: number, y: number) => OverlayContainer;
    rectangle: (x: number, y: number, w: number, h: number, color: number, alpha?: number) => OverlayRect;
    text: (x: number, y: number, text: string, style: object) => OverlayText;
  };
  time: {
    delayedCall: (delay: number, cb: () => void) => OverlayTimerEvent;
  };
}

export interface OverlayContainer {
  add: (items: unknown | unknown[]) => void;
  setDepth: (depth: number) => this;
  destroy: () => void;
}

export interface OverlayRect {
  setInteractive: () => this;
  on: (event: string, cb: () => void) => void;
}

export interface OverlayText {
  setOrigin: (x: number, y?: number) => this;
}

export interface OverlayTimerEvent {
  remove: () => void;
}

/** Public player state surface required for applying merchant effects. */
export interface MerchantPlayerState {
  hp: number;
  maxHp: number;
  skillCharge: number;     // 0.0 – 1.0
  waveXpMultiplier: number; // applied to XP earned this wave
}

export interface MerchantOverlayCallbacks {
  onPick: (index: number) => void;
  onDismiss: () => void;
}

/** Pixel dimensions for the overlay cards. */
const CARD_WIDTH  = 180;
const CARD_HEIGHT = 120;
const CARD_GAP    = 24;
const OVERLAY_W   = 800;
const OVERLAY_H   = 600;
const DISMISS_MS  = 3000;
const CARD_COLORS = [0x2196f3, 0xff9800, 0x9c27b0] as const;

/** Heal amount applied by the 'heal' option. */
export const HEAL_AMOUNT = 50;
/** Charge added (fraction of max) by the 'charge' option. */
export const CHARGE_BONUS = 0.30;
/** XP multiplier factor applied by the 'xp' option. */
export const XP_MULTIPLIER = 2;

/**
 * Apply the effect of a merchant option to player state (pure mutation, no Phaser dependency).
 * Returns the mutated state for convenience.
 */
export function applyMerchantOption(
  kind: MerchantOption['kind'],
  state: MerchantPlayerState,
): MerchantPlayerState {
  switch (kind) {
    case 'heal':
      state.hp = Math.min(state.maxHp, state.hp + HEAL_AMOUNT);
      break;
    case 'charge':
      state.skillCharge = Math.min(1, state.skillCharge + CHARGE_BONUS);
      break;
    case 'xp':
      state.waveXpMultiplier *= XP_MULTIPLIER;
      break;
  }
  return state;
}

export class MerchantOverlay {
  private scene: OverlayScene;
  private payload: MerchantPayload;
  private playerState: MerchantPlayerState;
  private callbacks: MerchantOverlayCallbacks;
  private container: OverlayContainer | null = null;
  private timer: OverlayTimerEvent | null = null;
  private dismissed = false;

  constructor(
    scene: OverlayScene,
    payload: MerchantPayload,
    playerState: MerchantPlayerState,
    callbacks: MerchantOverlayCallbacks,
  ) {
    this.scene = scene;
    this.payload = payload;
    this.playerState = playerState;
    this.callbacks = callbacks;
  }

  show(): void {
    const cx = OVERLAY_W / 2;
    const cy = OVERLAY_H / 2;

    const container = this.scene.add.container(0, 0).setDepth(200);
    this.container = container;

    // Semi-transparent dark background
    const bg = this.scene.add.rectangle(cx, cy, OVERLAY_W, OVERLAY_H, 0x000000, 0.75);
    bg.setInteractive(); // absorb clicks under the overlay
    container.add(bg);

    // Title
    const title = this.scene.add
      .text(cx, cy - 130, '⚔ 波次補給 ⚔', {
        fontSize: '32px',
        color: '#ffd54f',
        fontFamily: 'sans-serif',
      })
      .setOrigin(0.5);
    container.add(title);

    // Sub-hint
    const hint = this.scene.add
      .text(cx, cy - 88, '選擇一項補給 (3秒後自動跳過)', {
        fontSize: '16px',
        color: '#aaa',
        fontFamily: 'sans-serif',
      })
      .setOrigin(0.5);
    container.add(hint);

    // MERCHANT-FAKE-BUFF FIX (2026-05-21): card count is now dynamic (2 or 3)
    // because the scene may strip the "fake" charge option. Re-derive the
    // total row width from `payload.options.length` so the row stays centered
    // whether we show 2 or 3 cards.
    const cardCount = this.payload.options.length;
    const totalWidth = CARD_WIDTH * cardCount + CARD_GAP * (cardCount - 1);
    const startX = cx - totalWidth / 2 + CARD_WIDTH / 2;

    this.payload.options.forEach((opt, i) => {
      const cardX = startX + i * (CARD_WIDTH + CARD_GAP);
      const cardY = cy + 10;

      const card = this.scene.add
        .rectangle(cardX, cardY, CARD_WIDTH, CARD_HEIGHT, CARD_COLORS[i])
        .setInteractive();

      const label = this.scene.add
        .text(cardX, cardY, opt.label, {
          fontSize: '18px',
          color: '#fff',
          fontFamily: 'sans-serif',
          wordWrap: { width: CARD_WIDTH - 12 },
          align: 'center',
        })
        .setOrigin(0.5);

      container.add([card, label]);

      card.on('pointerup', () => {
        if (this.dismissed) return;
        this._dismiss();
        applyMerchantOption(opt.kind, this.playerState);
        this.callbacks.onPick(i);
      });
    });

    // Auto-dismiss timer
    this.timer = this.scene.time.delayedCall(DISMISS_MS, () => {
      if (this.dismissed) return;
      this._dismiss();
      this.callbacks.onDismiss();
    });
  }

  private _dismiss(): void {
    this.dismissed = true;
    if (this.timer) { this.timer.remove(); this.timer = null; }
    if (this.container) { this.container.destroy(); this.container = null; }
  }
}
